import { NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
  Chart,
  ChartItem,
  registerables
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  differenceInCalendarDays,
  differenceInCalendarISOWeeks,
  differenceInCalendarMonths,
  differenceInCalendarYears,
  differenceInHours,
  endOfISOWeek,
  format,
  isValid,
  setISOWeek,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfSecond,
  startOfWeek,
  startOfYear
} from 'date-fns';
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { ChartInteractionHandler } from 'src/app/classes/widget/chartInteractionHandler';

Chart.register(...registerables, zoomPlugin, ChartDataLabels, annotationPlugin);

export const defaultColors = [
  '#1F77B4',
  '#0fd7fa',
  '#FF7F0E',
  '#2CA02C',
  '#ff1f9e',
  '#D62728',
  '#d9e009',
  '#9467BD',
  '#8ab4ed',
  '#96e5f2',
  '#ffac63',
  '#98DF8A',
  '#f291c8',
  '#FF9896',
  '#ecf089',
  '#C5B0D5',
  '#17BECF',
  '#BCBD22',
  '#E377C2',
  '#7F7F7F',
  '#8C564B',
  '#AEC7E8',
  '#FFBB78',
  '#C49C94',
  '#393B79',
];

// Synthesis rows comparing projects carry `p`: the project becomes the series, or splits each series
// (one stack per project for stacked types, a series keeping its color across the stacks).
export function rowSeries(row: { s?: any; p?: any }, stacked: boolean): { label: any; color: any; stack?: any } {
  if (row.p === undefined) return { label: row.s, color: row.s };
  if (row.s === undefined || row.s === null || row.s === '') return { label: row.p, color: row.p };

  const label = `${row.s} · ${row.p}`;
  return stacked ? { label, color: row.s, stack: row.p } : { label, color: label };
}

// "Cumulative" (design.cumulative): each series as its running total, the forecast going on from the last observed
// total (its band adds up the bounds: on the safe side); the target, a level marked with the date it is reached.
export interface CumulativeDesign {
  enabled?: boolean;
  target?: number | null;
}

const xKey = (x: any): number[] => (x !== null && typeof x === 'object'
  ? [x.year ?? 0, x.month ?? 0, x.week ?? 0, x.date ?? 0, x.hour ?? 0]
  : [Number(x) || 0]);

function byX(a: any, b: any): number {
  const [ka, kb] = [xKey(a.x), xKey(b.x)];
  const index = ka.findIndex((value, i) => value !== kb[i]);
  return index < 0 ? 0 : ka[index]! - kb[index]!;
}

export function cumulate(rows: any[]): any[] {
  const groups = new Map<string, any[]>();
  rows.forEach(row => {
    const key = JSON.stringify([row.s, row.pid ?? row.p]);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  });

  return [...groups.values()].flatMap(group => {
    let total = 0;
    const history = group.filter(row => row.kind !== 'forecast').sort(byX).map(row => {
      total += Number(row.y) || 0;
      return { ...row, y: total };
    });
    let y = total, lower = total, upper = total;
    const forecast = group.filter(row => row.kind === 'forecast').sort(byX).map(row => {
      y += Number(row.y) || 0;
      lower += Number(row.lower) || 0;
      upper += Number(row.upper) || 0;
      return { ...row, y, lower, upper };
    });
    return [...history, ...forecast];
  });
}

// The first row whose running total reaches the target, history or forecast; null when none does.
export function targetReached(rows: any[], target: number): any {
  return [...rows].sort(byX).find(row => Number(row.y) >= target) ?? null;
}

// A date of the X axis in words: dd/MM/yyyy, "S12 2026" for a week, MM/yyyy for a month.
export function xText(x: any): string {
  if (x === null || typeof x !== 'object') return `${x ?? ''}`;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  if (x.week !== undefined) return `S${x.week} ${x.year}`;
  if (x.date !== undefined) return `${pad(x.date)}/${pad((x.month ?? 0) + 1)}/${x.year}`;
  if (x.month !== undefined) return `${pad(x.month + 1)}/${x.year}`;
  return `${x.year ?? ''}`;
}

// The click on a point (design.interactivity): on with its switch (display), then a redirection or a zoom; a chart
// saved before the switch is on when it had a mode.
export function interactionModeOf(design: any): 'redirection' | 'zoom' | null {
  const interactivity = design?.interactivity;
  const mode = interactivity?.interactionMode;
  const enabled = interactivity?.display ?? (!!mode && mode !== 'none');
  if (!enabled) return null;
  return mode === 'zoom' ? 'zoom' : 'redirection';
}

// Sections saved before their switch (in place): the interaction on when a mode was chosen, the annotation when it
// drew something, the colours as they were (the default palette when the switch is off).
export function migrateChartDesign(design: any): void {
  const { interactivity, annotation, colors } = design ?? {};
  if (interactivity && interactivity.display === undefined) interactivity.display = interactionModeOf(design) !== null;
  if (annotation && annotation.display === undefined) annotation.display = !!(annotation.addLine || annotation.addBox);
  if (colors && colors.display === undefined) colors.display = true;
}

// "Distribution 100 %" (barDistribution): a horizontal bar variant of a chart.
export interface DistributionDesign {
  sort?: 'value' | 'category';              // default value
  showPercent?: boolean;                    // default true
}

const MIN_SEGMENT_LABEL = 6;                // %: a narrower segment gets no label

// Removed graph types, drawn as the type that replaced them.
export const GRAPH_TYPE_ALIASES: Readonly<Record<string, string>> = { barLimit: 'barGrouped' };

export class ChartHandlerV2 {
  // Variables
  chart: Chart | undefined;
  chartData: { x?: any; y?: number }[] = [];
  chartDesign: any;
  chartCtx: ChartItem = {} as ChartItem;
  chartType: 'line' | 'bar' | 'pie' | 'doughnut' | 'scatter' | 'bubble' | 'polarArea' | 'radar' = 'line';
  graphType: string = 'columnStacked';
  darkMode: boolean = true;
  zoomMode: string = 'xy';
  labels: string[] = [];
  isStacked: boolean = false;
  isLinear: boolean = false;
  galleryTool!: GalleryTool;
  // Translated texts of the forecast: the "today" line, and the range of a projected point ({lower}, {upper}).
  forecastTexts: { today: string; range: string; target: string; reached: string } =
    { today: '', range: '{lower} – {upper}', target: '{value}', reached: '{value} · {date}' };

  theme = {
    gridColor: '#e0e0e0',
    color: '#333333',
  };

  displayFormats = {
    standard: {
      datetime: 'yyyy-MM-dd HH:mm:ss',
      millisecond: 'HH:mm:ss.SSS',
      second: 'HH:mm:ss',
      minute: 'HH:mm',
      hour: 'dd HH\'h\'',
      day: 'MMM dd',
      week: 'yyyy II',
      month: 'yyyy MM',
      quarter: '\'Q\'Q - yyyy',
      year: 'yyyy',
    },
    periodic: {
      datetime: 'yyyy-MM-dd HH:mm:ss',
      millisecond: 'SSS',
      second: 'ss',
      minute: 'mm',
      hour: 'HH',
      day: 'eeee',
      week: 'II',
      month: 'MMM',
      quarter: '\'Q\'Q',
      year: 'yyyy',
    },
  };

  private zoomCallback?: (newQuery: any) => void;
  private interactionHandler: ChartInteractionHandler;
  private router?: Router;
  private currentZoomType: 'X' | 'Y' | null = null;
  // Chart.js runs outside Angular: its listeners (wheel, mousemove, resize…) never check the whole app.
  // What changes the app (click actions, zoom state) comes back in through inAngular().
  private zone?: NgZone;

  constructor(zone?: NgZone) {
    this.interactionHandler = new ChartInteractionHandler();
    this.zone = zone;
  }

  private convertToDate(dateObj: any): Date {
    if (dateObj instanceof Date) {
      return dateObj;
    }

    if (typeof dateObj === 'string' || typeof dateObj === 'number') {
      const result = new Date(dateObj);
      return result;
    }

    const result = new Date(
      dateObj.year ?? 1970,
      dateObj.month ?? 0,
      dateObj.date ?? 1,
      dateObj.hour ?? 0,
      dateObj.minute ?? 0,
      dateObj.second ?? 0,
      dateObj.millisecond ?? 0
    );

    return result;
  }

  private startOfPeriod(date: Date, period: string): Date {
    switch (period) {
      case 'year': return startOfYear(date);
      case 'month': return startOfMonth(date);
      case 'week': return startOfWeek(date, { weekStartsOn: 1 });
      case 'day':
      case 'date': return startOfDay(date);
      case 'hour': return startOfHour(date);
      case 'minute': return startOfMinute(date);
      case 'second': return startOfSecond(date);
      default: return date;
    }
  }

  private convertMomentToDateFnsFormat(momentFormat: string): string {
    if (!momentFormat) return 'yyyy-MM-dd';

    return momentFormat
      .replace(/YYYY/g, 'yyyy')
      .replace(/YY/g, 'yy')
      .replace(/MMMM/g, 'LLLL')
      .replace(/MMM/g, 'LLL')
      .replace(/MM/g, 'MM')
      .replace(/M/g, 'M')
      .replace(/DDDD/g, 'eeee')
      .replace(/DDD/g, 'eee')
      .replace(/DD/g, 'dd')
      .replace(/D/g, 'd')
      .replace(/dddd/g, 'eeee')
      .replace(/ddd/g, 'eee')
      .replace(/dd/g, 'dd')
      .replace(/d/g, 'd')
      .replace(/HH/g, 'HH')
      .replace(/H/g, 'H')
      .replace(/hh/g, 'hh')
      .replace(/h/g, 'h')
      .replace(/mm/g, 'mm')
      .replace(/m/g, 'm')
      .replace(/ss/g, 'ss')
      .replace(/s/g, 's')
      .replace(/SSS/g, 'SSS')
      .replace(/SS/g, 'SS')
      .replace(/S/g, 'S')
      .replace(/A/g, 'a')
      .replace(/a/g, 'a')
      .replace(/\[Q\]Q/g, 'QQQ')
      .replace(/Q/g, 'Q')
      .replace(/WW/g, 'ww')
      .replace(/W/g, 'w');
  }

  setGraphType(type: string | string[]) {
    if (typeof type === 'string') type = GRAPH_TYPE_ALIASES[type] ?? type;
    this.graphType = Array.isArray(type) ? type.join(',') : type;
    this.chartType = 'line';

    if (type.includes('bar') || type.includes('column') || type.includes('mix')) {
      this.chartType = 'bar';
    } else if (type.includes('Scatter')) {
      this.chartType = 'scatter';
    } else if (type == 'pie' || type == 'doughnut') {
      this.chartType = type;
    }

    this.isStacked = this.graphType.includes('Stacked') || this.graphType === 'barDistribution';
  }

  destroy() {
    if (this.chart) this.chart.destroy();
  }

  plugins(chartDesign: { legend: any; title: any; yAxis: { format: string; }; format: { display: any; fontSize: any; }; annotation: { display?: boolean; addLine: any; label: any; backgroundColor: string; yMin: any; yMax: any; addBox: any; xMin: any; xMax: any; }; }) {
    let legendOptions = chartDesign.legend;
    let titleOptions = chartDesign.title;

    // The zoom / pan state shows the zoom back buttons of the dashboard: refresh it once done.
    const refreshState = () => this.inAngular(() => {});
    let zoom = {
      pan: {
        enabled: true,
        onPanComplete: refreshState,
      },
      zoom: {
        onZoomComplete: refreshState,
        wheel: {
          enabled: true,
          modifierKey: 'ctrl',
        },
        pinch: {
          enabled: true,
          modifierKey: 'ctrl',
        },
        drag: {
          enabled: true,
          modifierKey: 'ctrl',
        },
        mode: this.zoomMode,
      },
    };

    let result: any = {
      pointStyle: false,
      borderWidth: 0,
      zoom
    };

    if (legendOptions) {
      result.legend = {
        display: legendOptions.display,
        position: legendOptions.position,
        align: legendOptions.align,
        labels: {
          usePointStyle: legendOptions.usePointStyle,
          font: {
            size: legendOptions.fontSize,
          },
          color: this.theme.color,
        },
      };
    }

    if (titleOptions) {
      result.title = {
        text: titleOptions.text,
        display: false,
        position: titleOptions.position,
        align: titleOptions.align,
        font: {
          size: titleOptions.fontSize,
        },
        color: this.theme.color,
      };
    }

    if (chartDesign.yAxis.format != 'text') {
      result.datalabels = {
        display: chartDesign.format.display,
        font: {
          size: chartDesign.format.fontSize || 10,
        },
        color: this.theme.color,
        formatter: (value: { x: any; y: any; }, context: any) => {
          let v = this.graphType.includes('bar') ? value.x : value.y;

          if (v !== undefined)
            return this.formatValue(chartDesign.format, v);

          return this.formatValue(chartDesign.format, value);
        },
      };
    } else {
      result.datalabels = {
        display: chartDesign.format.display,
        font: {
          size: chartDesign.format.fontSize || 10,
        },
        color: this.theme.color,
        formatter: (value: { x: any; y: any; }, context: any) => {
          let v = this.graphType.includes('bar') ? value.x : value.y;

          if (v !== undefined)
            return this.formatValue(chartDesign.format, v);

          return this.formatValue(chartDesign.format, value);
        },
      };
    }

    if (chartDesign.annotation && chartDesign.annotation.display !== false) {
      result.annotation = {
        annotations: {},
      };
      if (chartDesign.annotation.addLine) {
        result.annotation.annotations['line'] = {
          type: 'line',
          label: {
            content: chartDesign.annotation.label,
            backgroundColor: chartDesign.annotation.backgroundColor,
            display: true,
          },
          yMin: chartDesign.annotation.yMin,
          yMax: chartDesign.annotation.yMax,
          borderColor: chartDesign.annotation.backgroundColor,
          borderWidth: 2,
        };
      }

      if (chartDesign.annotation.addBox) {
        result.annotation.annotations['line'] = {
          // Indicates the type of annotation
          type: 'box',
          xMin: chartDesign.annotation.xMin,
          xMax: chartDesign.annotation.xMax,
          yMin: chartDesign.annotation.yMin,
          yMax: chartDesign.annotation.yMax,
          label: {
            content: chartDesign.annotation.label,
            display: true,
          },
          backgroundColor: chartDesign.annotation.backgroundColor + '66',
        };
      }
    }

    if (this.chartType == 'pie' || this.chartType == 'doughnut') {
      // result.legend.display = false;
      // result.datalabels.labels = {
      //     index: {
      //         color: '#404040',
      //         font: {
      //             size: 10,
      //         },
      //         formatter: (value, ctx) => {
      //             let v;
      //             if (value.y) v= this.formatValue(chartDesign.format, value.y)
      //             else v= this.formatValue(chartDesign.format, value);
      //             return ctx.chart.data.labels[ctx.dataIndex]
      //         },
      //         align: 'end',
      //         anchor: 'end',
      //     },
      //     value: {
      //         color: '#404040',
      //         backgroundColor: '#fff',
      //         borderColor: '#fff',
      //         borderWidth: 2,
      //         borderRadius: 4,
      //         padding: 0,
      //         align: 'bottom',
      //       },
      // }
    }
    return result;
  }

  scales(chartDesign: { xAxis: any; yAxis: any; format: any; isDate: any; }) {
    if (this.chartType !== 'pie' && this.chartType !== 'doughnut') {
      let xOption = chartDesign.xAxis || {};
      let yOption = chartDesign.yAxis || {};
      let x: any = {
        bounds: 'ticks',
        grid: {
          color: this.theme.gridColor,
          display: !this.isStacked || (this.isStacked && this.isLinear),
        },
        ticks: {
          color: this.theme.color,
          align: 'center',
          font: {
            size: xOption.fontSize || 10,
          },
        },
        color: this.theme.color,
        stacked: this.isStacked,
      };

      if (this.isStacked && !this.isLinear && this.labels.length) {
        x.labels = this.labels;

        x.grid.callback = (value: any, index: string | number, ticks: any) => {
          const idx = typeof index === 'number' ? index : parseInt(index as string, 10);
          return this.labels.length ? this.labels[idx] : value;
        };

        x.grid.maxTicksLimit = this.labels.length;
      }

      let y: any = {
        grid: {
          color: this.theme.gridColor,
        },
        ticks: {
          color: this.theme.color,
          stepSize: yOption.stepSize || 0,
          font: {
            size: yOption.fontSize || 10,
          },
          callback: (value: { x: any; y: any; }, index: any, ticks: any) => {
            let v = this.graphType.includes('bar') ? value.x : value.y;
            if (v !== undefined) return this.formatValue(chartDesign.format, v);
            return this.formatValue(chartDesign.format, value);
          },
        },
        color: this.theme.color,
        stacked: this.isStacked,
        type: yOption.type || 'linear',
      };

      if (xOption.title) {
        x.title = {
          display: true,
          text: xOption.title,
        };
      }

      if (yOption.title) {
        y.title = {
          display: true,
          text: yOption.title,
          tick: {
            display: true,
          },
        };
      }

      if (chartDesign.isDate) {
        x.type = 'time';

        if (xOption.timeFormat.format) {
          const convertedFormat = this.convertMomentToDateFnsFormat(xOption.timeFormat.format);

          x.time = {
            displayFormats: {
              [xOption.timeFormat.binning]: convertedFormat,
            },
            unit: xOption.timeFormat.binning,
          };
        } else {
          let displayFormats = xOption.timeFormat.periodic ? this.displayFormats.periodic : this.displayFormats.standard;

          x.time = {
            displayFormats: displayFormats,
            unit: xOption.timeFormat.binning,
          };
        }
      }

      if (yOption.useMinMax) {
        y.min = yOption.min;
        y.max = yOption.max;
      }

      if (this.graphType.includes('bar')) {
        return {
          x: y,
          y: x,
        };
      } else {
        return {
          x,
          y,
        };
      }
    }
    return {};
  }

  sortDataset(design: { type: string; options: any[]; }, datasets: any[]) {
    if (design.type == 'string' || design.type == 'number') {
      let sortType: string | undefined = undefined;
      let sortDir: number | undefined = undefined;

      design.options.forEach((o) => {
        if (o.name == 'sort') sortType = o.value;
        if (o.name == 'order') sortDir = o.value == 'asc' ? 1 : -1;
      });

      if (!sortType || sortDir === undefined) return;
      if (sortType == 'value')
        datasets.sort((a, b) => (a.label > b.label ? sortDir! : 0));
    }
  }

  // Value calculation logic
  formatValue(options: any, value: any) {
    let formatted = value;
    if (!options) return formatted;

    if (options.type == 'number') {
      if (!isNaN(value)) {

        if (!isNaN(options.addition)) {
          formatted += parseInt(options.addition);
        }

        if (options.multiplier !== undefined) {
          formatted = options.multiplier * value;
        }

        if (options.decimal >= 0) {
          formatted = formatted.toFixed(options.decimal);
        } else {
          if (this.isNumeric(value.toString()) == 'int')
            formatted = parseInt(formatted);
          else if (this.isNumeric(value.toString()) == 'float')
            formatted = parseFloat(formatted) < 10 && parseFloat(formatted) > -10 ? parseFloat(formatted).toFixed(2) : parseFloat(formatted).toFixed(0);
        }

        if (options.percentage) {
          let totalY = this.chartData.reduce((sum, d) => sum + (d.y ?? 0), 0);

          if (totalY !== 0) {
            formatted =
              ((parseFloat(formatted) / totalY) * 100).toFixed(
                options.decimal >= 0 ? options.decimal : 2
              ) + '%';
          } else {
            formatted = '0%';
          }
        }
      }
    } else if (options.type == 'date') {
      if (isValid(new Date(value))) {
        if (options.dateFormat && options.dateFormat != 'custom') {
          formatted = format(new Date(value), this.convertMomentToDateFnsFormat(options.dateFormat));
        } else if (options.dateFormat && options.dateFormat == 'custom') {
          formatted = format(new Date(value), this.convertMomentToDateFnsFormat(options.customDateFormat));
        }
      }
    } else if (options.type == 'timing') {
      if (!isNaN(value) && options.timeFormat) {
        if (!isNaN(options.addition)) {
          value += parseInt(options.addition);
        }
        if (options.timeUnit == 'millisecond') {
          value *= 0.001;
        }
        let day,
          hour,
          minute,
          second,
          mod = value;
        formatted = '';

        if (options.timeFormat.includes('d')) {
          day = Math.floor(value / (3600 * 24));
          mod = value % (3600 * 24);
          formatted = day + 'd ';
        }
        if (options.timeFormat.includes('h')) {
          hour = Math.floor(mod / 3600);
          formatted += hour + 'h ';
          mod = mod % 3600;
        }

        if (options.timeFormat.includes('m')) {
          minute = Math.floor(mod / 60);
          mod = mod % 60;
          formatted += minute.toString() + 'm ';
        }

        if (options.timeFormat.includes('s')) {
          second = Math.round(mod);
          formatted += second.toString() + 's';
        }
      } else {
        // Removing extra decimals at the top and bottom of y axis when zooming
        if (this.isNumeric(value) == 'int') formatted = parseInt(formatted);
        else if (this.isNumeric(value) == 'float') {
          formatted =
            parseFloat(formatted) < 10 && parseFloat(formatted) > -10
              ? formatted.toFixed(2)
              : formatted.toFixed(0);
        }
      }
    } else if (options.type == 'text' && this.isNumeric(value)) {
      // Removing extra decimals at the top and bottom of y axis when zooming
      if (this.isNumeric(value) == 'int') formatted = parseInt(formatted);
      else if (this.isNumeric(value) == 'float') {
        formatted =
          parseFloat(formatted) < 10 && parseFloat(formatted) > -10
            ? formatted.toFixed(1)
            : formatted.toFixed(0);
      }
    }

    if (options.display && options.prefix) {
      if (options.prefix != '0' || formatted.toString() != '0')
        // this is to avoid having 00 value where it should be 0
        formatted = options.prefix.toString() + formatted;
    }
    if (options.display && options.suffix) {
      formatted = formatted + options.suffix.toString();
    }
    return formatted;
  }

  setChartCtx(ctx: ChartItem) {
    this.chartCtx = ctx;
  }

  updateChart(chartData: any, chartDesign: any, chartQuery: any, galleryTool: GalleryTool) {
    if (!this.chartCtx) return console.log('No context for this chart');
    if (!chartData || !chartData.length) chartData = [];

    this.updateThemeColors();

    this.chartDesign = chartDesign;
    // With a forecast, the current period is its forecast row (what is there plus the rest): its partial history,
    // a bar or a point lower than the others, would read as a drop.
    this.chartData = chartData.some((d: any) => d.kind === 'forecast') ? chartData.filter((d: any) => !d.partial) : chartData;
    if (chartDesign?.cumulative?.enabled) this.chartData = cumulate(this.chartData);
    this.galleryTool = galleryTool;
    let datasetNames = chartDesign.datasetNames && Array.isArray(chartDesign.datasetNames) ? chartDesign.datasetNames : [];
    let labels: any[] = [];
    let datasets: any[] = [];
    let indexAxis = this.graphType.includes('bar') ? 'y' : 'x';
    this.isStacked = this.graphType.includes('Stacked') || this.graphType === 'barDistribution';
    let colorOptions = chartDesign.colors?.display === false ? {} : chartDesign.colors || {};

    if (!chartQuery.aggregation) return console.warn("no aggregation found");

    let aggregations = Object.keys(chartQuery.aggregation);
    let colorList = [];

    if (!colorOptions.colorList) {
      colorOptions.colorList = {};
    }

    let order: string | undefined = undefined;
    let type: string = '';
    let periodic: any = undefined;
    let binning: string = '';
    let xDateFormat: string | null = null;
    let xFormat: string = this.chartDesign.xAxis ? this.chartDesign.xAxis.format : null;

    if (xFormat == "date") {
      xDateFormat = this.chartDesign.xAxis ? this.chartDesign.xAxis.dateFormat : null;

      if (xDateFormat == 'Custom')
        xDateFormat = this.chartDesign.xAxis.dateCustomFormat || null;

      //for retrocompatibility
      if (typeof xDateFormat != 'string') xDateFormat = null;
    }

    let yFormat = this.chartDesign.yAxis ? this.chartDesign.yAxis.format : '';

    if (yFormat) {
      // A format chosen without its settings (an insight saved before they were added): the raw value.
      this.chartDesign.format = this.chartDesign[yFormat + 'Format'] ?? {};
      this.chartDesign.format.type = yFormat;
    }

    let field = chartQuery['x-axis'] || chartQuery['field'];

    if (field && Object.keys(field).length)
      Object.keys(field).forEach((k) => {
        type = field[k].type;
        order = field[k].options.order;
        binning = field[k].options.binning;
        periodic = field[k].options.periodic;
      });

    if (order) {
      this.chartDesign.isDate = false;

      if (type == 'date') {
        this.chartDesign.isDate = true;
        this.chartData.forEach((d: any) => {
          d.sort = this.convertToDate(d.x).toISOString();
        });
        if (order == 'ascending')
          this.chartData.sort((a: any, b: any) => {
            return a.sort > b.sort ? 1 : -1;
          });
        else this.chartData.sort((a: any, b: any) => {
          return a.sort <= b.sort ? 1 : -1;
        });
      } else if (this.chartDesign.yAxis && this.chartDesign.yAxis.enableCustomOrder) {
        const customOrder = this.chartDesign.yAxis.customOrder;
        const orderMap = new Map<any, any>(customOrder.map((x: any, i: any) => [x, i]));

        const { inCustomOrder, notInCustomOrder } = this.chartData.reduce(
          (acc: { inCustomOrder: any[]; notInCustomOrder: any[] }, d: any) => {
            if (orderMap.has(d.x)) {
              acc.inCustomOrder.push(d);
            } else {
              acc.notInCustomOrder.push(d);
            }

            return acc;
          },
          { inCustomOrder: [] as any[], notInCustomOrder: [] as any[] }
        );

        inCustomOrder.sort((a, b) => orderMap.get(a.x) - orderMap.get(b.x));

        this.chartData = [...inCustomOrder, ...notInCustomOrder];
      } else {
        if (order == 'ascending')
          this.chartData.sort((a: any, b: any) => {
            return a.sort > b.sort ? 1 : 0;
          });
        else
          this.chartData.sort((a: any, b: any) => {
            return a.sort <= b.sort ? 1 : 0;
          });
      }
    }
    this.isLinear = type == 'date';

    if (type == 'date' && !xDateFormat) {
      switch (binning) {
        case 'year':
          xFormat = 'yyyy';
          break;
        case 'month':
          xFormat = periodic ? 'MMM' : 'yyyy MM';
          break;
        case 'week':
          xFormat = periodic ? 'II' : 'yyyy II';
          break;
        case 'date':
          xFormat = periodic ? 'do' : 'MM do';
          break;
        case 'day':
          xFormat = periodic ? 'eeee' : 'MMM dd';
          break;
        case 'hour':
          xFormat = periodic ? 'HH' : 'dd HH';
          break;
        case 'minute':
          xFormat = periodic ? 'mm' : 'HH:mm';
          break;
        case 'second':
          xFormat = periodic ? 'ss' : 'HH:mm:ss';
          break;
        case 'default':
          xFormat = periodic ? 'MMM' : 'yyyy MM';
          break;
      }

      this.chartDesign.xAxis.timeFormat = {
        binning,
        periodic,
      };
    } else if (type == 'date') {
      xFormat = this.convertMomentToDateFnsFormat(xDateFormat ?? '');
      this.chartDesign.xAxis.timeFormat = {
        format: xFormat,
        binning,
        periodic,
      };
    }

    this.labels = []; // only to be filled for non-linear labels

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const currentDate = new Date().getDate();
    const isPeriodic = periodic === true;
    const isTypeDate = type === 'date';

    this.chartData.forEach((d: any) => {
      if (!d.x) return;

      if (isTypeDate) {
        let localDate: Date;

        if (d.x.week !== undefined) {
          // ISO week of its ISO week year (Jan 4 is always in week 1).
          localDate = endOfISOWeek(setISOWeek(new Date(d.x.year || currentYear, 0, 4), d.x.week));
        } else if (isPeriodic) {
          localDate = this.startOfPeriod(
            this.convertToDate({
              ...d.x,
              year: currentYear,
              month: d.x.month || currentMonth,
              date: d.x.date || currentDate
            }),
            binning
          );
        } else {
          localDate = this.startOfPeriod(
            this.convertToDate(binning === 'day' ? d.x : { year: 1970, month: 1, date: 1, ...d.x }),
            binning
          );

        }
        d._x = this.dateToUTCTimestamp(localDate);
      } else {
        d._x = d.x.toString();
      }

      if (!this.isLinear && this.labels.indexOf(d._x) === -1) {
        this.labels.push(d._x);
      }

      // Forecast rows only need their position here: addForecast draws them after the history.
      if (d.kind === 'forecast') return;

      const isPie = this.chartType == 'pie' || this.chartType == 'doughnut';
      // Pie/doughnut legend: text only (a date bin is a timestamp).
      if (isPie && typeof d._x === 'number') d._x = xText(d.x);

      if (
        (chartQuery['series'] && Object.keys(chartQuery['series']).length) ||
        d.s ||
        (d.p !== undefined && !isPie)
      ) {
        let data = indexAxis == 'x' ? { x: d._x, y: d.y } : { y: d._x, x: d.y };
        const series = rowSeries(d, this.isStacked);
        let set = datasets.find((_d) => _d.label == series.label);

        if (set) {
          set.data.push(data);
        } else {
          let color = this.getDatasetColor(series.color, colorOptions);

          let fill: any = 'none',
            radius = 2,
            opacity = 'FF';

          if (this.graphType.includes('Area')) {
            opacity = '88';
            fill = datasets.length != 0 ? datasets.length - 1 : 'origin';
          }

          if (this.graphType.includes('continious')) {
            opacity = '00';
            radius = 0;
          }

          datasets.push({
            label: series.label,
            ...(series.stack !== undefined ? { stack: series.stack } : {}),
            backgroundColor: color + opacity,
            borderColor: color,
            fill,
            radius,
            data: [data],
          });
        }
      } else {
        if (this.chartType !== 'pie' && this.chartType !== 'doughnut') {
          aggregations.forEach((a, i) => {
            let y = i > 0 ? d['y' + i] : d.y;
            let data = indexAxis == 'x' ? { x: d._x, y } : { y: d._x, x: y };

            if (!datasets[i])
              datasets.push({ data: [], label: a });

            let fill: any = 'none',
              radius = 2,
              opacity = '00';

            if (this.graphType.includes('Area')) {
              opacity = '88';
              fill = 'origin';
            }

            if (this.graphType.includes('continious')) {
              radius = 0;
            }

            datasets[i].radius = radius;
            datasets[i].fill = fill;

            let datasetName = datasetNames.find((dataset: { key: string; }) => dataset.key == a);
            let label = datasetName ? datasetName.value : a;
            let color = this.getDatasetColor(label, colorOptions);

            datasets[i].label = label;
            datasets[i].opacity = opacity;
            datasets[i].borderColor = color;
            datasets[i].backgroundColor = color;
            datasets[i].data.push(data);
          });
        } else {
          let data = indexAxis == 'x' ? { x: d._x, y: d.y } : { y: d._x, x: d.y };
          // Rows comparing projects: one ring per project.
          let ring = d.p === undefined ? 0 : datasets.findIndex((set) => set.label === d.p);
          if (ring < 0) ring = datasets.length;

          if (!datasets[ring])
            datasets[ring] = { data: [], label: d.p ?? aggregations[0] };

          datasets[ring].data.push(data);

          let colors: any[] = [];

          datasets[ring].data.forEach((d: { x: string | number; }) => {
            colors.push(this.getDatasetColor(`${d.x}`, colorOptions));
          });

          datasets[ring].backgroundColor = colors;
        }
      }

      if ((this.chartType == 'pie' || this.chartType == 'doughnut') && (d.p === undefined || !labels.includes(d._x))) {
        labels.push(d._x);
      }
    });

    // Rings of projects with different categories: align every ring on the shared labels.
    if ((this.chartType == 'pie' || this.chartType == 'doughnut') && this.chartData.some((d: any) => d.p !== undefined)) {
      datasets.forEach((set) => {
        set.data = labels.map((label) => set.data.find((point: any) => point.x === label) ?? { x: label, y: 0 });
        set.backgroundColor = labels.map((label) => this.getDatasetColor(`${label}`, colorOptions));
      });
    }

    // The distribution reworks the datasets built above.
    if (this.graphType === 'barDistribution') {
      const distribution = this.distributionDatasets(chartDesign.distribution ?? {}, colorOptions);
      datasets = distribution.datasets;
      labels = distribution.bars;
      this.labels = distribution.bars;
    }

    const forecastAt = this.addForecast(datasets, chartQuery, indexAxis, binning);

    let data: any = {
      datasets,
    };
    if (labels.length) {
      data.labels = labels;
    }

    if (this.graphType.includes('continious')) {
      Chart.defaults.elements.point.pointStyle = 'line';
    } else {
      Chart.defaults.elements.point.pointStyle = 'circle';
    }

    Chart.defaults.elements.line.borderWidth = 2;
    Chart.defaults.elements.line.fill = true;

    if (this.chart)
      this.chart.destroy();

    let options: any = {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      elements: {
        bar: {
          borderWidth: 0,
        },
        arc: {
          borderWidth: 0,
        },
        line: {
          tension: 0,
        },
      },
      indexAxis: this.graphType.includes('bar') ? 'y' : 'x',
      plugins: this.plugins(chartDesign),
      scales: this.scales(chartDesign),
      parsing: {
        key: 'y',
      },
    };

    if (forecastAt !== null) this.forecastOptions(options, forecastAt, indexAxis);
    if (chartDesign?.cumulative?.enabled && Number(chartDesign.cumulative.target) > 0) this.targetOptions(options, Number(chartDesign.cumulative.target), indexAxis);
    if (this.graphType === 'barDistribution') this.distributionOptions(options);

    // Actions on option
    options.onClick = (event: MouseEvent) => {
      if (!this.chart) return;

      const interactionMode = interactionModeOf(chartDesign);
      if (!interactionMode) return;

      const points = this.chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
      if (!points.length) return;

      const point = points[0];
      if (!point) return;

      const dataset = this.chart.data.datasets[point.datasetIndex];
      if (!dataset?.data) return;

      const data = dataset.data[point.index];

      if (interactionMode === 'zoom') {
        const zoomTargetAxis = chartDesign.interactivity.zoomTargetAxis;

        if (zoomTargetAxis === 'Y' && this.currentZoomType === 'Y') {
          console.warn('❌ [ZOOM] BLOQUÉ - Zoom sur Y déjà effectué');
          return;
        }
        const newQuery = this.interactionHandler.handleZoom(
          zoomTargetAxis,
          chartDesign,
          chartQuery,
          data
        );

        if (newQuery && this.zoomCallback) {
          this.currentZoomType = zoomTargetAxis;
          const callback = this.zoomCallback;
          this.inAngular(() => callback(newQuery));
        } else {
          console.warn('⚠️ Aucune action trouvée pour ce cas');
        }
      }
      else if (interactionMode === 'redirection') {
        const redirectionTarget = chartDesign.interactivity.redirectionTarget;

        if (this.router) {
          const router = this.router;
          this.inAngular(() => this.interactionHandler.handleRedirection(
            redirectionTarget,
            chartQuery,
            data,
            router
          ));
        } else {
          console.error('⚠️ Router non défini pour la redirection');
        }
      }
    };

    const create = () => new Chart(this.chartCtx, {
      type: this.chartType,
      plugins: chartDesign.format || this.graphType === 'barDistribution'
        ? [ChartDataLabels, zoomPlugin]
        : [zoomPlugin],
      options,
      data,
    });
    this.chart = this.zone ? this.zone.runOutsideAngular(create) : create();
  }

  isNumeric(num: string) {
    if (num == '') return false;

    if (/^-?\d+$/.test(num)) {
      return 'int';
    } else if (/^-?\d+\.\d+$/.test(num)) {
      return 'float';
    }

    return false;
  }

  /**
   * Calcule un index déterministe dans defaultColors à partir d'un titre,
   * de sorte qu'un même titre obtienne toujours la même couleur.
   */
  private getColorIndexFromTitle(title: string): number {
    let hash = 0;

    for (let i = 0; i < title.length; i++) {
      hash = (hash * 31 + title.charCodeAt(i)) | 0;
    }

    return Math.abs(hash) % defaultColors.length;
  }

  /**
   * Retourne la couleur d'un dataset : celle définie dans colorOptions.colorList
   * si elle existe pour ce titre, sinon une couleur de defaultColors calculée
   * à partir du titre.
   *
   * La clé est normalisée via getTagName pour qu'une même entité obtienne la
   * même couleur quel que soit le type de graphique (barres, camembert, etc.),
   * où la valeur peut être un nom d'affichage ou un nom de tag interne.
   */
  // Forecast rows: a dashed continuation of their series, then a confidence band (lower/upper, filled between).
  // Returns where the history ends (the "today" line), or null without forecast rows.
  private addForecast(datasets: any[], chartQuery: any, indexAxis: string, binning: string): any {
    const rows = this.chartData.filter((d: any) => d.kind === 'forecast' && d._x !== undefined);
    if (!rows.length) return null;

    const isPie = this.chartType == 'pie' || this.chartType == 'doughnut';
    const isBar = this.chartType == 'bar';
    const bySeries = (d: any) => (chartQuery['series'] && Object.keys(chartQuery['series']).length) || d.s || (d.p !== undefined && !isPie);
    const point = (at: any, value: any) => indexAxis == 'x' ? { x: at, y: value } : { y: at, x: value };
    // Chronological position on the X axis (Y for horizontal bars): the rows may come in any order
    // (a week binning sorts on the year only), so neither the last element nor the row order can be trusted.
    const position = (at: any) => this.isLinear ? Number(at) : this.labels.indexOf(at);
    const chronological = (a: any, b: any) => position(a._x) - position(b._x);
    const history = [...datasets];

    history.forEach((set, index) => {
      const own = rows.filter((d: any) => bySeries(d) ? rowSeries(d, this.isStacked).label === set.label : index === 0).sort(chronological);
      if (!own.length) return;

      // Lines and areas start from the latest observed point, so the forecast continues the curve; only when it is
      // the bin right before the forecast: across empty bins the joining line would look like a projection of the past.
      const latest = set.data.reduce((best: any, p: any) => !best || position(p[indexAxis]) > position(best[indexAxis]) ? p : best, null);
      const joined = latest && this.nextBin(latest[indexAxis], (own[0] as any)._x, binning, position);
      const last = isBar || !joined ? [] : [latest];
      const color = set.borderColor;
      // Value labels like the history (the datalabels plugin shows them when `display` is unset),
      // except on the joining point and the band.
      const labelDisplay = this.chartDesign?.format?.display ?? true;
      const stack = (name: string) => set.stack !== undefined || this.isStacked ? { stack: `${name}-${index}` } : {};

      datasets.push({
        label: set.label,
        forecast: true,
        ...(set.stack !== undefined ? { stack: set.stack } : {}),
        data: [...last, ...own.map((d: any) => ({ ...point(d._x, d.y), lower: d.lower, upper: d.upper }))],
        borderColor: color,
        backgroundColor: isBar ? color + '66' : 'transparent',
        borderDash: [6, 4],
        borderWidth: isBar ? 1 : 2,
        fill: false,
        radius: set.radius ?? 2,
        datalabels: { display: (context: any) => context.dataIndex < last.length ? false : labelDisplay },
      });

      const band = (key: 'lower' | 'upper') => ({
        label: set.label,
        forecast: true,
        band: true,
        type: 'line',
        ...stack(`forecast-${key}`),
        data: [...last, ...own.map((d: any) => point(d._x, d[key]))],
        borderColor: 'transparent',
        backgroundColor: color + '22',
        pointRadius: 0,
        fill: key === 'upper' ? '-1' : false,
        datalabels: { display: false },
      });
      datasets.push(band('lower'), band('upper'));
    });

    const past = this.chartData.filter((d: any) => d.kind !== 'forecast' && d._x !== undefined).map((d: any) => d._x);
    if (!past.length) return null;
    return this.isLinear ? Math.max(...past) : [...this.labels].reverse().find((label) => past.includes(label)) ?? null;
  }

  // Distribution 100 %: one full bar per project (one in all otherwise), one segment per category, in %.
  private distributionDatasets(settings: DistributionDesign, colorOptions: any): { datasets: any[]; bars: any[] } {
    const rows = this.chartData.filter((d: any) => d.kind !== 'forecast' && d.x !== undefined && d.x !== null);
    const barOf = (d: any) => d.p ?? '';
    const categoryOf = (d: any) => d._x ?? `${d.x}`;
    const amount = (d: any) => Number(d.y) || 0;

    const bars = [...new Set(rows.map(barOf))];
    const totals = new Map<any, number>();
    const sums = new Map<any, number>();
    rows.forEach((d: any) => {
      totals.set(barOf(d), (totals.get(barOf(d)) ?? 0) + amount(d));
      sums.set(categoryOf(d), (sums.get(categoryOf(d)) ?? 0) + amount(d));
    });

    const categories = [...sums.keys()];
    if (settings.sort === 'category') categories.sort((a, b) => `${a}`.localeCompare(`${b}`));
    else categories.sort((a, b) => sums.get(b)! - sums.get(a)!);

    const showPercent = settings.showPercent !== false;
    const single = bars.length === 1;
    const datasets = categories.map((category) => {
      const data = bars.map((bar) => {
        const part = rows.filter((d: any) => barOf(d) === bar && categoryOf(d) === category).reduce((total: number, d: any) => total + amount(d), 0);
        const total = totals.get(bar) ?? 0;
        return { y: bar, x: total ? part / total * 100 : 0 };
      });
      const color = this.getDatasetColor(`${category}`, colorOptions);

      return {
        // A single bar: the legend says each share.
        label: single ? `${category} ${Math.round(data[0]!.x)} %` : category,
        backgroundColor: color,
        borderColor: color,
        data,
        datalabels: {
          display: (context: any) => showPercent && context.dataset.data[context.dataIndex].x >= MIN_SEGMENT_LABEL,
          formatter: (point: any) => `${Math.round(point.x)} %`,
          color: '#fff',
          anchor: 'center',
          align: 'center',
        },
      };
    });

    return { datasets, bars };
  }

  // Full width = 100 %, the legend below the bars.
  private distributionOptions(options: any): void {
    const x = options.scales?.x;
    if (x) {
      x.min = 0;
      x.max = 100;
    }

    options.plugins ??= {};
    options.plugins.legend = { ...options.plugins.legend, position: options.plugins.legend?.position ?? 'bottom' };
  }

  // Whether `next` is the bin right after `at`: same binning on a date axis, the next label otherwise.
  private nextBin(at: any, next: any, binning: string, position: (value: any) => number): boolean {
    if (!this.isLinear) return position(next) - position(at) === 1;

    const later = new Date(Number(next));
    const earlier = new Date(Number(at));
    switch (binning) {
      case 'hour': return differenceInHours(startOfHour(later), startOfHour(earlier)) === 1;
      case 'day': return differenceInCalendarDays(later, earlier) === 1;
      case 'week': return differenceInCalendarISOWeeks(later, earlier) === 1;
      case 'month': return differenceInCalendarMonths(later, earlier) === 1;
      case 'year': return differenceInCalendarYears(later, earlier) === 1;
      default: return true;
    }
  }

  // The target of a running total: a level, labelled with the date it is (or will be) reached; the value axis reaches it.
  private targetOptions(options: any, target: number, indexAxis: string): void {
    const valueAxis = indexAxis === 'x' ? 'y' : 'x';
    const reached = targetReached(this.chartData, target);
    const format = (value: number) => this.chartDesign?.format ? this.formatValue(this.chartDesign.format, value) : value;
    const prefix = reached?.kind === 'forecast' ? '≈ ' : '';
    const label = reached ? this.forecastTexts.reached.replace('{date}', prefix + xText(reached.x)) : this.forecastTexts.target;
    const content = label.replace('{value}', `${format(target)}`);
    options.plugins ??= {};
    const plugins = options.plugins;
    plugins.annotation ??= {};
    const annotation = plugins.annotation;

    const color = this.getCSSVariable('--primary-color') || '#3B82F6';

    annotation.annotations = {
      ...annotation.annotations,
      target: {
        type: 'line',
        [valueAxis + 'Min']: target,
        [valueAxis + 'Max']: target,
        borderColor: color,
        borderWidth: 2,
        borderDash: [6, 4],
        label: {
          display: true, content, position: 'end', backgroundColor: color, color: '#FFFFFF',
          borderRadius: 999, padding: { x: 10, y: 4 }, font: { size: 11, weight: 'bold' },
        },
      },
    };

    const scale = options.scales?.[valueAxis];
    if (scale) scale.suggestedMax = Math.max(Number(scale.suggestedMax) || 0, target * 1.05);
  }

  // A dotted "today" line where the history ends; the forecast stays out of the legend and the band out of tooltips.
  private forecastOptions(options: any, at: any, indexAxis: string): void {
    options.plugins ??= {};
    const plugins = options.plugins;
    plugins.annotation ??= {};
    const annotation = plugins.annotation;
    annotation.annotations = {
      ...annotation.annotations,
      forecastToday: {
        type: 'line',
        [indexAxis + 'Min']: at,
        [indexAxis + 'Max']: at,
        borderColor: '#9E9E9E',
        borderWidth: 1,
        borderDash: [4, 4],
        label: {
          display: !!this.forecastTexts.today,
          content: this.forecastTexts.today,
          position: 'start',
          backgroundColor: 'transparent',
          color: '#9E9E9E',
          font: { size: 10 },
        },
      },
    };

    plugins.legend ??= {};
    plugins.legend.labels = { ...plugins.legend.labels, filter: (item: any, data: any) => !data.datasets[item.datasetIndex]?.forecast };

    // A projected point: its range under its value, as the axis formats it.
    const format = (value: number) => this.chartDesign?.format ? this.formatValue(this.chartDesign.format, value) : Math.round(value);
    const tooltip = plugins.tooltip ?? {};
    plugins.tooltip = {
      ...tooltip,
      filter: (item: any) => !item.dataset?.band,
      callbacks: {
        ...tooltip.callbacks,
        afterLabel: (item: any) => item.dataset?.forecast && item.raw?.lower !== undefined
          ? this.forecastTexts.range.replace('{lower}', `${format(item.raw.lower)}`).replace('{upper}', `${format(item.raw.upper)}`)
          : '',
      },
    };
  }

  private getDatasetColor(title: string, colorOptions: any): string {
    // Nettoyage de la valeur brute : un espace, un accent composé/décomposé ou
    // une casse différente d'un graphique à l'autre produirait une clé (et donc
    // une couleur) différente pour la même entité.
    const cleaned = `${title}`.normalize('NFC').trim();
    const key = this.galleryTool ? this.galleryTool.getTagName(cleaned) : cleaned;

    if (colorOptions && colorOptions.colorList && colorOptions.colorList[key]) {
      return colorOptions.colorList[key];
    }

    return defaultColors[this.getColorIndexFromTitle(key)]!;
  }

  private getCSSVariable(variableName: string): string {
    const value = getComputedStyle(document.body)
      .getPropertyValue(variableName)
      .trim();

    return value;
  }

  private updateThemeColors() {
    const gridColor = this.getCSSVariable('--chart-gridline-color');
    const textColor = this.getCSSVariable('--chart-text-color');

    this.theme = {
      gridColor: gridColor || '#e0e0e0',
      color: textColor || '#333333',
    };
  }

  private dateToUTCTimestamp(date: Date): number {
    return Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    );
  }

  setRouter(router: Router) {
    this.router = router;
  }

  resetZoomTracking() {
    this.currentZoomType = null;
  }

  setZoomCallback(callback: (newQuery: any) => void) {
    this.zoomCallback = callback;
  }

  private inAngular(action: () => void): void {
    if (this.zone) this.zone.run(action);
    else action();
  }
}
